/**
 * Hoffmaster Fluid Carousel Widget Handler
 *
 * Handles Swiper initialization, responsive configuration, auxiliary navigation,
 * and all interactive behavior for the fluid carousel widget.
 *
 * @package Hoffmaster
 * @since 1.0.0
 */

class HoffmasterFluidCarouselHandler extends elementorModules.frontend.handlers.Base {
	getDefaultSettings() {
		return {
			selectors: {
				wrapper: '.hm-fluid-carousel',
				swiperContainer: '.hm-fluid-carousel__swiper',
				slide: '.hm-fluid-carousel__slide',
				pagination: '.hm-fluid-carousel__pagination',
				prevArrow: '.hm-fluid-carousel__arrow--prev',
				nextArrow: '.hm-fluid-carousel__arrow--next',
				auxNavLink: '.hm-fluid-carousel__aux-nav-link',
				srAnnounce: '.hm-fluid-carousel__sr-announce',
			},
			baseSwiperConfig: {
				slidesPerView: 'auto',
				spaceBetween: 32,
				centeredSlides: false,
				slidesOffsetBefore: 0,
				normalizeSlideIndex: true,
				slideToClickedSlide: false,
				speed: 600,
				grabCursor: true,
				freeMode: false,
				navigation: {
					prevEl: '.hm-fluid-carousel__arrow--prev',
					nextEl: '.hm-fluid-carousel__arrow--next',
				},
				pagination: {
					el: '.hm-fluid-carousel__pagination',
					clickable: true,
					type: 'bullets',
					renderBullet: function (index, className) {
						return '<span class="' + className + '" data-slide-index="' + index + '"></span>';
					},
				},
				a11y: {
					enabled: true,
					prevSlideMessage: 'Previous slide',
					nextSlideMessage: 'Next slide',
					firstSlideMessage: 'This is the first slide',
					lastSlideMessage: 'This is the last slide',
					paginationBulletMessage: 'Go to slide {{index}}',
				},
				keyboard: {
					enabled: true,
					onlyInViewport: true,
				},
				breakpoints: {},
				on: {},
			},
		};
	}

	getDefaultElements() {
		const selectors = this.getSettings('selectors');
		return {
			$wrapper: this.$element.find(selectors.wrapper),
			$swiperContainer: this.$element.find(selectors.swiperContainer),
			$slides: this.$element.find(selectors.slide),
			$pagination: this.$element.find(selectors.pagination),
			$prevArrow: this.$element.find(selectors.prevArrow),
			$nextArrow: this.$element.find(selectors.nextArrow),
			$auxNavLinks: this.$element.find(selectors.auxNavLink),
		};
	}

	bindEvents() {
		if (!this.elements.$wrapper.length || !this.elements.$swiperContainer.length) {
			return;
		}

		// Cleanup previous instance
		this.cleanup();

		// Initialize carousel
		this.initFluidCarousel();

		// Bind resize handler
		this.bindResizeHandler();
	}

	initFluidCarousel() {
		const wrapper = this.elements.$wrapper[0];
		const swiperContainer = this.elements.$swiperContainer[0];

		// Destroy existing instance if re-initializing
		if (swiperContainer.swiper) {
			swiperContainer.swiper.destroy(true, true);
		}

		// Apply responsive slide widths
		this.applySlideWidths();

		// Calculate slidesOffsetAfter
		const containerWidth = swiperContainer.offsetWidth;
		const firstSlide = this.elements.$slides[0];
		const slideWidth = firstSlide ? firstSlide.offsetWidth : 800;

		// Get current gap
		const responsiveConfig = this.buildResponsiveConfig();
		const currentGap = this.getCurrentGap(responsiveConfig);

		// Offset calculation
		const slidesOffsetAfter = Math.max(0, containerWidth - slideWidth + currentGap);

		// Build configuration
		const baseSwiperConfig = this.getSettings('baseSwiperConfig');
		const config = {
			...baseSwiperConfig,
			speed: parseInt(wrapper.dataset.animationSpeed, 10) || 600,
			breakpoints: responsiveConfig,
			slidesOffsetAfter: slidesOffsetAfter,
			watchSlidesProgress: true,
			watchOverflow: true,
			on: {
				init: (swiper) => {
					// Store swiper instance reference for use in init callback
					this.swiperInstance = swiper;
					this.updateAuxiliaryNav(0);
				},
				slideChange: () => {
					this.updateAuxiliaryNav(this.swiperInstance.activeIndex);
				},
			},
		};

		// Conditionally disable navigation/pagination
		const showPagination = this.elements.$wrapper.hasClass('hm-fluid-carousel--has-pagination');
		const showArrows = this.elements.$wrapper.hasClass('hm-fluid-carousel--has-arrows');

		if (!showPagination) {
			config.pagination = false;
		}

		if (!showArrows) {
			config.navigation = false;
		}

		// Initialize Swiper
		this.swiperInstance = new Swiper(swiperContainer, config);

		// Fix pagination bullet count
		if (showPagination && this.swiperInstance.pagination && this.swiperInstance.pagination.el) {
			this.fixPaginationBullets();
		}

		// Initialize auxiliary navigation
		if (this.elements.$wrapper.hasClass('hm-fluid-carousel--has-aux-nav')) {
			this.initAuxiliaryNav();
		}
	}

	buildResponsiveConfig() {
		const wrapper = this.elements.$wrapper[0];
		const gapsStr = wrapper.dataset.gaps || '{}';
		const gaps = JSON.parse(gapsStr);

		const breakpointMap = {
			widescreen: 1920,
			laptop: 1366,
			tablet_extra: 1200,
			tablet: 1024,
			mobile_extra: 880,
			mobile: 768,
		};

		const breakpoints = {};

		Object.keys(breakpointMap).forEach((bp) => {
			const gapValue = gaps[bp];
			if (gapValue) {
				breakpoints[breakpointMap[bp]] = {
					spaceBetween: parseInt(gapValue) || 32,
				};
			}
		});

		// Add desktop/default gap if available
		if (gaps.desktop) {
			breakpoints[0] = {
				spaceBetween: parseInt(gaps.desktop) || 32,
			};
		}

		return breakpoints;
	}

	getCurrentGap(responsiveConfig) {
		const width = window.innerWidth;
		const breakpoints = Object.keys(responsiveConfig).sort((a, b) => b - a);

		for (const bp of breakpoints) {
			if (width >= parseInt(bp)) {
				return responsiveConfig[bp].spaceBetween || 32;
			}
		}

		return 32; // Default
	}

	applySlideWidths() {
		const wrapper = this.elements.$wrapper[0];
		const slideWidthsStr = wrapper.dataset.slideWidths || '{}';
		const slideWidths = JSON.parse(slideWidthsStr);

		// Determine current breakpoint
		let currentWidth;
		const width = window.innerWidth;

		if (width >= 1920) {
			currentWidth = slideWidths.widescreen || slideWidths.desktop || '800px';
		} else if (width >= 1366) {
			currentWidth = slideWidths.laptop || slideWidths.desktop || '800px';
		} else if (width >= 1200) {
			currentWidth = slideWidths.tablet_extra || slideWidths.tablet || '600px';
		} else if (width >= 1024) {
			currentWidth = slideWidths.tablet || '600px';
		} else if (width >= 880) {
			currentWidth = slideWidths.mobile_extra || slideWidths.mobile || '100vw';
		} else if (width >= 768) {
			currentWidth = slideWidths.mobile || '100vw';
		} else {
			currentWidth = slideWidths.mobile || '100vw';
		}

		// Convert 100vw to 100% on tablet and below
		if (width <= 1024 && currentWidth === '100vw') {
			currentWidth = '100%';
		}

		// Apply width to all slides
		this.elements.$slides.css('width', currentWidth);
	}

	initAuxiliaryNav() {
		this.elements.$auxNavLinks.each((index, link) => {
			jQuery(link).on('click', (e) => {
				e.preventDefault();
				const slideIndex = parseInt(link.dataset.slideIndex, 10);
				this.swiperInstance.slideTo(slideIndex, this.swiperInstance.params.speed);
			});
		});
	}

	updateAuxiliaryNav(activeIndex) {
		this.elements.$auxNavLinks.each((index, link) => {
			const $link = jQuery(link);
			if (index === activeIndex) {
				$link.addClass('is-active');
				$link.attr('aria-current', 'true');
			} else {
				$link.removeClass('is-active');
				$link.removeAttr('aria-current');
			}
		});

		// Announce to screen readers
		this.announceSlideChange(activeIndex);
	}

	announceSlideChange(activeIndex) {
		// Safety check: ensure swiper instance exists
		if (!this.swiperInstance || !this.swiperInstance.slides) {
			return;
		}

		const selectors = this.getSettings('selectors');
		let liveRegion = this.elements.$swiperContainer.find(selectors.srAnnounce)[0];

		if (!liveRegion) {
			liveRegion = document.createElement('div');
			liveRegion.className = 'hm-fluid-carousel__sr-announce sr-only';
			liveRegion.setAttribute('aria-live', 'polite');
			liveRegion.setAttribute('aria-atomic', 'true');
			this.elements.$swiperContainer[0].appendChild(liveRegion);
		}

		const total = this.swiperInstance.slides.length;
		liveRegion.textContent = `Showing slide ${activeIndex + 1} of ${total}`;
	}

	fixPaginationBullets() {
		const actualSlideCount = this.swiperInstance.slides.length;

		this.swiperInstance.on('paginationUpdate', (swiper, paginationEl) => {
			const bullets = paginationEl.querySelectorAll('.swiper-pagination-bullet');

			bullets.forEach((bullet, index) => {
				if (index >= actualSlideCount) {
					bullet.style.display = 'none';
				} else {
					bullet.style.display = '';
				}
			});
		});

		// Trigger initial update
		this.swiperInstance.pagination.update();
	}

	bindResizeHandler() {
		let resizeTimeout;

		this.resizeHandler = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				this.applySlideWidths();

				if (this.swiperInstance && !this.swiperInstance.destroyed) {
					const swiperContainer = this.elements.$swiperContainer[0];
					const responsiveConfig = this.buildResponsiveConfig();

					// Recalculate slidesOffsetAfter
					const newContainerWidth = swiperContainer.offsetWidth;
					const newFirstSlide = this.elements.$slides[0];
					const newSlideWidth = newFirstSlide ? newFirstSlide.offsetWidth : 800;
					const newCurrentGap = this.getCurrentGap(responsiveConfig);

					const newSlidesOffsetAfter = Math.max(0, newContainerWidth - newSlideWidth + newCurrentGap);

					this.swiperInstance.params.slidesOffsetAfter = newSlidesOffsetAfter;
					this.swiperInstance.update();
				}
			}, 150);
		};

		jQuery(window).on('resize', this.resizeHandler);
	}

	cleanup() {
		if (this.resizeHandler) {
			jQuery(window).off('resize', this.resizeHandler);
		}

		if (this.swiperInstance && !this.swiperInstance.destroyed) {
			this.swiperInstance.destroy(true, true);
		}
	}

	onDestroy() {
		this.cleanup();
	}
}

jQuery(window).on('elementor/frontend/init', () => {
	elementorFrontend.elementsHandler.attachHandler(
		'hm_fluid_carousel',
		HoffmasterFluidCarouselHandler
	);

	// Re-initialize on device mode change in Elementor editor
	if (typeof elementorFrontend !== 'undefined') {
		elementorFrontend.on('components:init', () => {
			let previousDeviceMode = elementorFrontend.getCurrentDeviceMode();

			setInterval(() => {
				const currentDeviceMode = elementorFrontend.getCurrentDeviceMode();
				if (currentDeviceMode !== previousDeviceMode) {
					previousDeviceMode = currentDeviceMode;

					jQuery('.elementor-widget-hm_fluid_carousel').each(function () {
						const handler = elementorFrontend.elementsHandler.getHandler(
							'hm_fluid_carousel',
							this
						);
						if (handler && handler.bindEvents) {
							handler.bindEvents();
						}
					});
				}
			}, 500);
		});
	}
});
